import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Circle, Download, FileText, Loader2, PackageX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Order Confirmed", "Generating", "Delivered"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

export default function TrackPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [lookupId, setLookupId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API}/track/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setError("We couldn't find that order. Check the link from your email."));
  }, [orderId]);

  const lookup = (e) => {
    e.preventDefault();
    const id = lookupId.trim().split("/").pop();
    if (id) navigate(`/track/${id}`);
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-16" data-testid="track-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-lg rounded-[2rem] bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-8 sm:p-10">
        <img src="/logo.png" alt="Vowyo" className="h-9 w-auto mb-8" />

        {!orderId && (
          <form onSubmit={lookup} className="py-4" data-testid="track-lookup-form">
            <h1 className="font-display font-bold text-3xl">Track your song</h1>
            <p className="text-sm text-plum font-medium mt-2">Paste your order ID or tracking link from your confirmation email.</p>
            <input
              data-testid="track-lookup-input"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="e.g. 6e613d49-… or the full tracking link"
              className="mt-5 w-full rounded-full bg-cream border-2 border-ink px-5 py-3.5 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow"
            />
            <button data-testid="track-lookup-btn" type="submit"
              className="btn-pop mt-4 rounded-full bg-pink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
              Track Order
            </button>
          </form>
        )}

        {error && (
          <div className="text-center py-8">
            <PackageX size={40} className="mx-auto text-tangerine" />
            <p className="mt-4 font-head font-semibold text-plum" data-testid="track-error">{error}</p>
          </div>
        )}
        {!order && !error && (
          <div className="text-center py-8"><Loader2 size={36} className="mx-auto text-pink animate-spin" /></div>
        )}

        {order && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-lilac">Tracking order</p>
            <h1 className="font-display font-bold text-3xl mt-1" data-testid="track-heading">
              A song for {order.recipient_name}
            </h1>
            <p className="text-sm text-plum font-medium mt-1.5">
              {order.occasion}{order.genre ? ` · ${order.genre}` : ""} · {inr(order.amount)} · {order.buyer_email}
            </p>

            <div className="mt-8 space-y-0">
              {STEPS.map((s, i) => {
                const current = STEPS.indexOf(order.order_status);
                const reached = i <= current;
                const ts = s === "Order Confirmed" ? order.created_at : s === "Generating" ? order.paid_at || order.created_at : order.delivered_at;
                return (
                  <div key={s} className="flex gap-4" data-testid={`track-step-${i}`}>
                    <div className="flex flex-col items-center">
                      {reached ? <CheckCircle2 size={26} className="text-mint shrink-0" fill="#00E5A3" stroke="#2D1A32" /> : <Circle size={26} className="text-ink/20 shrink-0" />}
                      {i < STEPS.length - 1 && <div className={`w-0.5 flex-1 my-1 ${i < current ? "bg-mint" : "bg-ink/10"}`} style={{ minHeight: 28 }} />}
                    </div>
                    <div className="pb-7">
                      <p className={`font-head font-bold ${reached ? "text-ink" : "text-lilac"}`}>{s}</p>
                      {reached && ts && <p className="text-xs text-lilac font-mono mt-0.5">{fmt(ts)}</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            {order.order_status === "Delivered" && (
              <div className="mt-2 rounded-2xl bg-mint/20 border-2 border-ink p-5" data-testid="track-delivered-box">
                <p className="font-head font-bold text-sm mb-3">Your song is ready:</p>
                <div className="flex flex-wrap gap-3">
                  {order.song_path && (
                    <a href={`${API}/files/${order.song_path}`} data-testid="track-download-mp3"
                      className="btn-pop inline-flex items-center gap-2 rounded-full bg-pink text-white font-head font-bold text-xs px-5 py-2.5 border-2 border-ink shadow-[3px_3px_0_#2D1A32]">
                      <Download size={14} /> Download MP3
                    </a>
                  )}
                  {order.card_path && (
                    <a href={`${API}/files/${order.card_path}`} data-testid="track-download-card"
                      className="btn-pop inline-flex items-center gap-2 rounded-full bg-sunny text-ink font-head font-bold text-xs px-5 py-2.5 border-2 border-ink shadow-[3px_3px_0_#2D1A32]">
                      <FileText size={14} /> Lyric Card PDF
                    </a>
                  )}
                </div>
                {order.lyrics && (
                  <pre className="mt-4 whitespace-pre-wrap font-sans text-sm text-plum italic">{order.lyrics}</pre>
                )}
              </div>
            )}

            <p className="mt-8 font-mono text-[10px] text-lilac break-all">Order {order.order_ref || order.order_id}</p>
          </>
        )}

        <Link to="/" data-testid="track-back-home" className="mt-6 inline-block text-xs font-mono text-lilac underline underline-offset-4 hover:text-pink transition-colors">
          ← Back to Vowyo
        </Link>
      </motion.div>
    </div>
  );
}
